import {
    Dictionary,
    DropDown,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';

import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';

import useAuth from 'hooks/useAuth';
import { MerchantFilterType } from 'models/api/merchant';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { Col, Row } from 'reactstrap';

type MerchantFilterProps = {
    orderStatuses: DropDown[];
    sources: DropDown[];
    outlets: DropDown[];
    channels: DropDown[];
    brandCodes: DropDown[];
    isFetchingOutlets: boolean;
    isFetchingChannels: boolean;
    getOrderStatusesQuery: UseQueryResult<DropDown[], unknown>;
    getDictsQuery: UseQueryResult<Dictionary<DropDown[]>, unknown>;
    isFetchingBrandCodes: boolean;
};

const MerchantFilter = ({
    orderStatuses,
    sources,
    outlets,
    channels,
    brandCodes,
    isFetchingOutlets,
    isFetchingChannels,
    getOrderStatusesQuery,
    getDictsQuery,
    isFetchingBrandCodes,
}: MerchantFilterProps) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();

    return (
        <>
            <ValidatedDropDownList<MerchantFilterType>
                name="statusCode"
                items={orderStatuses}
                isLoading={getOrderStatusesQuery.isFetching}
                disabled={!orderStatuses.length}
                label={t('BackOfficeOrderStatus')}
            />

            <ValidatedInput<MerchantFilterType>
                id="ds-order-info"
                name="orderInfo"
                label={t('BackOfficeClientInfo')}
            />
            <Row>
                <Col>
                    <BOValidatedDatePicker<MerchantFilterType>
                        id="ds-start-date"
                        name="sd"
                        inputProps={{ label: t('BackOfficeStartDate') }}
                    />
                </Col>
                <Col>
                    <BOValidatedDatePicker<MerchantFilterType>
                        id="ds-end-date"
                        name="ed"
                        inputProps={{ label: t('BackOfficeEndDate') }}
                    />
                </Col>
            </Row>
            {hasRole('merchantGetOutletsDropDown') && (
                <Row>
                    <Col>
                        <ValidatedDropDownList<MerchantFilterType>
                            name="outletId"
                            items={outlets}
                            isLoading={isFetchingOutlets}
                            disabled={!outlets.length}
                            label={t('BackOfficeOutlet')}
                        />
                    </Col>
                </Row>
            )}
            {hasRole('merchantBrandCodeFilter') && (
                <Row>
                    <Col>
                        <ValidatedDropDownList<MerchantFilterType>
                            name="brandCode"
                            items={brandCodes}
                            isLoading={!isFetchingBrandCodes}
                            disabled={!brandCodes.length}
                            label={t('BackOfficeBrandCode')}
                        />
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs="6">
                    <ValidatedDropDownList<MerchantFilterType>
                        name="source"
                        items={sources}
                        isLoading={getDictsQuery.isFetching}
                        disabled={!sources.length}
                        label={t('BackOfficeSource')}
                    />
                </Col>
                {hasRole('merchantGetChannels') && (
                    <Col xs="6">
                        <ValidatedDropDownList<MerchantFilterType>
                            name="channel"
                            items={channels}
                            isLoading={isFetchingChannels}
                            disabled={!channels.length}
                            label={t('BackOfficeChannel')}
                        />
                    </Col>
                )}
            </Row>
        </>
    );
};

export default MerchantFilter;
